/**
 * VersionIndicator - Indicatore versioni documento
 *
 * Mostra un'icona con il numero di versioni disponibili.
 * Visibile solo se il documento ha più di una versione.
 * Cliccando si apre un dropdown per navigare tra le versioni.
 *
 * UI: Icona clock + "X versions" con tooltip e dropdown
 */

import { Clock, Check } from "@untitledui/icons";
import { useTranslation } from "react-i18next";
import { cx } from "@/utils/cx";
import { useDocumentVersionCount, usePrefetchDocumentVersions, useDocumentVersions } from "@/hooks/api/use-document-versions";
import type { DocumentVersionResponse } from "@/types/api/documents";
import {
  TooltipTrigger,
  Tooltip,
  Button,
  Popover,
  DialogTrigger,
  Dialog,
} from "react-aria-components";
import { format } from "date-fns";
import { it, enUS } from "date-fns/locale";

interface VersionIndicatorProps {
  /** ID del documento */
  documentId: string;
  /** Callback quando si seleziona una versione (documentId, version) */
  onVersionSelect?: (documentId: string, version: number) => void;
  /** Dimensione dell'indicatore */
  size?: "sm" | "md";
  /** Classi CSS aggiuntive */
  className?: string;
  /** Se mostrare il label testuale */
  showLabel?: boolean;
  /** Conteggio versioni passato direttamente (evita fetch) */
  versionCount?: number;
  /** Versione attualmente visualizzata */
  currentVersion?: number;
}

/**
 * VersionIndicator - Mostra indicatore versioni con dropdown per navigazione
 */
export function VersionIndicator({
  documentId,
  onVersionSelect,
  size = "sm",
  className,
  showLabel = true,
  versionCount: externalVersionCount,
  currentVersion,
}: VersionIndicatorProps) {
  const { t, i18n } = useTranslation("pages:documents");
  const prefetchVersions = usePrefetchDocumentVersions();

  // Usa il conteggio esterno se fornito, altrimenti fetch
  const { data: versionData, isLoading: countLoading } = useDocumentVersionCount(documentId, {
    enabled: externalVersionCount === undefined,
  });

  // Fetch versions list for dropdown
  const { data: versionsData, isLoading: versionsLoading } = useDocumentVersions(documentId);

  const versionCount = externalVersionCount ?? versionData?.version_count ?? 1;
  const dateLocale = i18n.language === "it" ? it : enUS;

  // Non mostrare se solo una versione o loading
  if (versionCount <= 1) {
    return null;
  }

  // Prefetch versioni on hover per UX migliore
  const handleMouseEnter = () => {
    prefetchVersions(documentId);
  };

  const sizeClasses = {
    sm: "text-xs gap-1",
    md: "text-sm gap-1.5",
  };

  const iconSizeClasses = {
    sm: "h-3.5 w-3.5",
    md: "h-4 w-4",
  };

  const tooltipText = t("versions.tooltip", {
    count: versionCount,
    defaultValue: `Questo documento ha ${versionCount} versioni`,
  });

  const labelText = t("versions.count", {
    count: versionCount,
    defaultValue: `${versionCount} versions`,
  });

  return (
    <DialogTrigger>
      <TooltipTrigger delay={500}>
        <Button
          onHoverStart={handleMouseEnter}
          className={cx(
            "inline-flex items-center rounded-md px-1.5 py-0.5",
            "text-fg-quaternary hover:text-fg-secondary",
            "hover:bg-primary_hover transition-colors",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
            "cursor-pointer",
            sizeClasses[size],
            countLoading && "opacity-50",
            className
          )}
        >
          <Clock className={iconSizeClasses[size]} />
          {showLabel && <span>{labelText}</span>}
        </Button>
        <Tooltip
          placement="top"
          className={cx(
            "z-[60] bg-gray-800 text-gray-100",
            "px-2.5 py-1.5 rounded-md text-xs shadow-lg",
            "border border-gray-700",
            "animate-in fade-in-0 zoom-in-95"
          )}
        >
          {tooltipText}
        </Tooltip>
      </TooltipTrigger>
      <Popover
        placement="bottom start"
        className={cx(
          "z-50 bg-primary border border-secondary rounded-lg shadow-lg",
          "overflow-hidden min-w-[260px]",
          "animate-in fade-in-0 zoom-in-95"
        )}
      >
        <Dialog aria-label="Version history" className="outline-none">
          <div className="p-2">
            <p className="text-xs font-medium text-fg-quaternary px-2 py-1 mb-1">
              {t("versions.selectVersion", { defaultValue: "Seleziona versione" })}
            </p>
            {versionsLoading ? (
              <div className="px-2 py-3 text-center">
                <span className="text-sm text-fg-quaternary">{t("common:loading", "Caricamento...")}</span>
              </div>
            ) : versionsData?.versions ? (
              <div className="max-h-[240px] overflow-y-auto">
                {versionsData.versions.map((version) => (
                  <VersionDropdownItem
                    key={version.document_id}
                    version={version}
                    isSelected={version.version === currentVersion}
                    isCurrent={version.is_current}
                    dateLocale={dateLocale}
                    onSelect={() => onVersionSelect?.(version.document_id, version.version)}
                  />
                ))}
              </div>
            ) : (
              <div className="px-2 py-3 text-center">
                <span className="text-sm text-fg-quaternary">{t("versions.noVersions", "Nessuna versione")}</span>
              </div>
            )}
          </div>
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}

/**
 * VersionDropdownItem - Item nel dropdown delle versioni
 */
function VersionDropdownItem({
  version,
  isSelected,
  isCurrent,
  dateLocale,
  onSelect,
}: {
  version: DocumentVersionResponse;
  isSelected: boolean;
  isCurrent: boolean;
  dateLocale: typeof it | typeof enUS;
  onSelect: () => void;
}) {
  const { t } = useTranslation("pages:documents");

  const formattedDate = format(new Date(version.created_at), "d MMM yyyy, HH:mm", {
    locale: dateLocale,
  });

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cx(
        "w-full flex items-center justify-between gap-2 px-2 py-2 rounded-md",
        "cursor-pointer outline-none text-left",
        "hover:bg-primary_hover focus:bg-primary_hover transition-colors",
        isSelected && "bg-brand-50 dark:bg-brand-950"
      )}
    >
      <div className="flex flex-col gap-0.5 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-fg-primary text-sm">
            {t("versions.selector.version", {
              version: version.version,
              defaultValue: `Versione ${version.version}`,
            })}
          </span>
          {isCurrent && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-success-100 text-success-700 dark:bg-success-900 dark:text-success-300">
              {t("versions.selector.latest", "attuale")}
            </span>
          )}
        </div>
        <span className="text-xs text-fg-quaternary truncate">
          {formattedDate}
          {version.file_name && ` · ${version.file_name}`}
        </span>
      </div>

      {isSelected && (
        <Check className="h-4 w-4 text-brand-600 flex-shrink-0" />
      )}
    </button>
  );
}

/**
 * VersionIndicatorStatic - Versione statica senza fetch
 * Utile quando il conteggio è già disponibile
 */
export function VersionIndicatorStatic({
  versionCount,
  onClick,
  size = "sm",
  className,
}: {
  versionCount: number;
  onClick?: () => void;
  size?: "sm" | "md";
  className?: string;
}) {
  const { t } = useTranslation("pages:documents");

  if (versionCount <= 1) {
    return null;
  }

  const sizeClasses = {
    sm: "text-xs gap-1",
    md: "text-sm gap-1.5",
  };

  const iconSizeClasses = {
    sm: "h-3.5 w-3.5",
    md: "h-4 w-4",
  };

  const labelText = t("versions.count", {
    count: versionCount,
    defaultValue: `${versionCount} versions`,
  });

  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        "inline-flex items-center rounded-md px-1.5 py-0.5",
        "text-fg-quaternary hover:text-fg-secondary",
        "hover:bg-primary_hover transition-colors",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
        "cursor-pointer",
        sizeClasses[size],
        className
      )}
      title={t("versions.tooltip", {
        count: versionCount,
        defaultValue: `Questo documento ha ${versionCount} versioni`,
      })}
    >
      <Clock className={iconSizeClasses[size]} />
      <span>{labelText}</span>
    </button>
  );
}
